import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { LookupOption, LookupTypeCodes } from '../../../models/lookup.models';
import {
  ANY_SENIORITY_CODE,
  WizardRequestedResource,
} from '../../../models/request-wizard.models';
import { LookupService } from '../../../services/lookup.service';

/** "Any" seniority option, always shown first. */
const ANY_SENIORITY_OPTION: LookupOption = {
  value: ANY_SENIORITY_CODE,
  label: 'Any',
};

/** Seniority lookup codes excluded from the selectable requested-resource options. */
const EXCLUDED_SENIORITY_CODES = new Set<string>(['OTHER']);

/**
 * Requested-resource editor rendered as one pill per seniority level ("Any"
 * first), each carrying its requested count adjusted with the + / − controls.
 *
 * Stateless: the parent owns the array and receives a brand-new array on every
 * change via (rowsChange) — the component never mutates its input. Only
 * seniorities with a count > 0 appear in the emitted rows.
 */
@Component({
  selector: 'app-requested-resources-editor',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [MatIconModule],
  templateUrl: './requested-resources-editor.component.html',
  styleUrls: ['./requested-resources-editor.component.scss'],
})
export class RequestedResourcesEditorComponent {
  private lookupService = inject(LookupService);

  /** Current rows; the source of truth lives in the parent. */
  @Input() rows: WizardRequestedResource[] = [];

  /** Emits a new rows array whenever a count changes. */
  @Output() rowsChange = new EventEmitter<WizardRequestedResource[]>();

  /** One entry per seniority level, with "Any" first. */
  seniorityOptions: LookupOption[] = [ANY_SENIORITY_OPTION];

  constructor() {
    this.lookupService
      .getLookupOptions(LookupTypeCodes.SENIORITY)
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (options) => {
          this.seniorityOptions = [
            ANY_SENIORITY_OPTION,
            ...options.filter((o) => !EXCLUDED_SENIORITY_CODES.has(o.value)),
          ];
        },
        error: (error) => {
          console.error('Error loading seniority options:', error);
        },
      });
  }

  /** Splits the seniority pills into two columns (left fills first). */
  get pillColumns(): LookupOption[][] {
    const half = Math.ceil(this.seniorityOptions.length / 2);
    return [
      this.seniorityOptions.slice(0, half),
      this.seniorityOptions.slice(half),
    ];
  }

  /** Requested count for a seniority (0 when not yet requested). */
  countFor(seniorityCode: string): number {
    return this.rows.find((r) => r.seniorityCode === seniorityCode)?.count ?? 0;
  }

  increment(seniorityCode: string): void {
    this.setCount(seniorityCode, this.countFor(seniorityCode) + 1);
  }

  decrement(seniorityCode: string): void {
    this.setCount(seniorityCode, this.countFor(seniorityCode) - 1);
  }

  /**
   * Rebuilds the rows from the per-seniority counts — ordered by seniority
   * option and excluding zero counts — then emits the new array.
   */
  private setCount(seniorityCode: string, count: number): void {
    const counts = new Map(this.rows.map((r) => [r.seniorityCode, r.count]));
    const next = Math.max(0, count);
    if (next > 0) {
      counts.set(seniorityCode, next);
    } else {
      counts.delete(seniorityCode);
    }
    const rows = this.seniorityOptions
      .filter((opt) => (counts.get(opt.value) ?? 0) > 0)
      .map((opt) => ({
        count: counts.get(opt.value) as number,
        seniorityCode: opt.value,
      }));
    this.rowsChange.emit(rows);
  }
}
